// Oracle tree-sitter parser loader — lazy, module-level singleton. PRD §3.1
// GG3: only loaded when a connection whose dialect is oracle first parses SQL;
// never loaded on PG/MSSQL/MySQL/SQLite connections.
//
// The default loader is bundler-safe (`../shared/wasmLoader.ts`): the Electron
// renderer fetches the Vite `?url`-bundled `oracle.wasm` asset, while
// Node / Vitest / Electron main read `resources/tree-sitter/oracle.wasm` off
// the filesystem. Tests still inject a custom `OracleWasmLoader` via
// `setOracleWasmLoader(...)` to stub the bytes without touching disk.

import { Language, Parser } from 'web-tree-sitter';

import { loadGrammarWasmBytes } from '../shared/wasmLoader';

export type OracleWasmLoader = () => Promise<Uint8Array>;

let loader: OracleWasmLoader | null = null;
let parserPromise: Promise<Parser> | null = null;

export function setOracleWasmLoader(next: OracleWasmLoader | null): void {
  loader = next;
  // Reset the cached parser so a subsequent call re-initializes with the new
  // loader. This is primarily useful for tests; production code sets the
  // loader once at startup.
  parserPromise = null;
}

function defaultLoader(): Promise<Uint8Array> {
  // Bundler-safe: renderer fetches the Vite `?url` asset, node/main read off
  // disk. See `../shared/wasmLoader.ts`. The renderer no longer needs an
  // injected loader — the `?url` import is statically copied into the build.
  return loadGrammarWasmBytes(
    'oracle',
    import.meta.url,
    () => import('../../../../../resources/tree-sitter/oracle.wasm?url'),
  );
}

async function initParser(): Promise<Parser> {
  await Parser.init();
  const wasmBytes = await (loader ?? defaultLoader)();
  const language = await Language.load(wasmBytes);
  const parser = new Parser();
  parser.setLanguage(language);
  return parser;
}

export function getOracleParser(): Promise<Parser> {
  if (!parserPromise) {
    // A FAILED load (missing wasm, a corrupted download) must not be cached
    // forever: clear the slot so the next call retries instead of replaying
    // the same rejection for the life of the process. The rejection itself
    // still reaches every caller of this attempt.
    const attempt = initParser();
    parserPromise = attempt;
    attempt.catch(() => {
      if (parserPromise === attempt) parserPromise = null;
    });
  }
  return parserPromise;
}

// Test-only reset so specs can exercise the lazy-load path cleanly.
export function __resetOracleParserForTest(): void {
  parserPromise = null;
}
